import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import styled, { ThemeProvider } from "styled-components/macro";
import { useQuery } from "react-query";
import ResetZoomControl from "./ResetZoomControl";
import { STARTING_LOCATION } from "../../constants";
import ToggleBasemapControl from "./ToggleBasemapControl";
import debounce from "lodash.debounce";
import { lineColors } from "../../utils";
import ReactDOM from "react-dom";
import {
  jssPreset,
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import createTheme from "../../theme";
import Popup from "../../pages/publicMap/popup";
import { create } from "jss";
import { useSelector } from "react-redux";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const TimeseriesComparisonMap = ({
  selectedHuc10Locations,
  selectedLeftLocations,
  selectedRightLocations,
}) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const theme = useSelector((state) => state.themeReducer);
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const [map, setMap] = useState();

  const popUpRef = useRef(
    new mapboxgl.Popup({
      maxWidth: "400px",
      offset: 15,
      focusAfterOpen: false,
    })
  );
  const mapContainer = useRef(null); // create a reference to the map container

  const DUMMY_BASEMAP_LAYERS = [
    { url: "streets-v11", icon: "commute" },
    { url: "outdoors-v11", icon: "park" },
    { url: "satellite-streets-v11", icon: "satellite_alt" },
  ];

  const locationsLayer = {
    id: "locations",
    type: "circle",
    source: "locations",
    paint: {
      "circle-stroke-width": [
        "case",
        ["in", ["get", "index"], ["literal", selectedLeftLocations]],
        5,
        ["in", ["get", "index"], ["literal", selectedRightLocations]],
        5,
        1,
      ],
      "circle-stroke-color": [
        "case",
        ["in", ["get", "index"], ["literal", selectedLeftLocations]],
        lineColors.yellow,
        ["in", ["get", "index"], ["literal", selectedRightLocations]],
        lineColors.red,
        "black",
      ],
      "circle-radius": 7,
      "circle-color": [
        "case",
        ["==", ["get", "huc10"], "1405000204"],
        "#808000",
        ["==", ["get", "huc10"], "1405000111"],
        "#3cb44b",
        ["==", ["get", "huc10"], "1405000109"],
        "#3b00ad",
        ["==", ["get", "huc10"], "1405000106"],
        "#4363d8",
        ["==", ["get", "huc10"], "1018000101"],
        "#911eb4",
        ["==", ["get", "huc10"], "1405000102"],
        "#2CF5F7",
        ["==", ["get", "huc10"], "1018000103"],
        "#f032e6",
        ["==", ["get", "huc10"], "1405000101"],
        "#000075",
        ["==", ["get", "huc10"], "1405000206"],
        "#bcf60c",
        ["==", ["get", "huc10"], "1018000102"],
        "#fabebe",
        ["==", ["get", "huc10"], "1405000501"],
        "#008080",
        ["==", ["get", "huc10"], "1405000104"],
        "#e6beff",
        ["==", ["get", "huc10"], "1405000103"],
        "#9a6324",
        ["==", ["get", "huc10"], "1401000110"],
        "#fffac8",
        ["==", ["get", "huc10"], "1405000303"],
        "#800000",
        ["==", ["get", "huc10"], "1405000110"],
        "#aaffc3",
        lineColors.black,
      ],
    },
    lreProperties: {
      popup: {
        titleField: "description",
        excludeFields: ["index", "description"],
      },
    },
  };

  const locationsLabelsLayer = {
    id: "locations-labels",
    type: "symbol",
    source: "locations",
    minzoom: 7,
    layout: {
      "text-field": [
        "format",
        ["get", "description"],
        {}, // Use default formatting
        "\n",
        {},
        ["get", "id"],
        {
          "text-font": ["literal", ["DIN Offc Pro Italic"]],
          "font-scale": 0.85,
        },
      ],
      "text-size": 12,
      "text-offset": [0, -3.75],
      "text-font": ["literal", ["Roboto Black", "Arial Unicode MS Bold"]],
      visibility: "visible",
    },
    paint: {
      "text-color": "rgb(49,49,49)",
      "text-halo-color": "rgba(255,255,255,1)",
      "text-halo-width": 3,
    },
  };

  const huc10Fill = {
    id: "huc-10-boundaries-fill",
    name: "HUC 10 Boundaries",
    type: "fill",
    source: "huc-10-boundaries",
    "source-layer": "Yampa_HUC10-c6nelo",
    paint: {
      "fill-color": [
        "case",
        ["==", ["get", "HUC10"], "1405000204"],
        "#808000",
        ["==", ["get", "HUC10"], "1405000111"],
        "#3cb44b",
        ["==", ["get", "HUC10"], "1405000109"],
        "#f58231",
        ["==", ["get", "HUC10"], "1405000106"],
        "#4363d8",
        ["==", ["get", "HUC10"], "1018000101"],
        "#911eb4",
        ["==", ["get", "HUC10"], "1405000102"],
        "#2CF5F7",
        ["==", ["get", "HUC10"], "1018000103"],
        "#f032e6",
        ["==", ["get", "HUC10"], "1405000101"],
        "#000075",
        ["==", ["get", "HUC10"], "1405000206"],
        "#bcf60c",
        ["==", ["get", "HUC10"], "1018000102"],
        "#fabebe",
        ["==", ["get", "HUC10"], "1405000501"],
        "#008080",
        ["==", ["get", "HUC10"], "1405000104"],
        "#e6beff",
        ["==", ["get", "HUC10"], "1405000103"],
        "#9a6324",
        ["==", ["get", "HUC10"], "1401000110"],
        "#fffac8",
        ["==", ["get", "HUC10"], "1405000303"],
        "#800000",
        ["==", ["get", "HUC10"], "1405000110"],
        "#aaffc3",
        lineColors.black,
      ],
      "fill-opacity": [
        "case",
        [
          "in",
          ["get", "HUC10"],
          [
            "literal",
            [
              "1405000204",
              "1405000111",
              "1405000109",
              "1405000106",
              "1018000101",
              "1405000102",
              "1018000103",
              "1405000101",
              "1405000206",
              "1018000102",
              "1405000501",
              "1405000104",
              "1405000103",
              "1401000110",
              "1405000303",
              "1405000110",
            ],
          ],
        ],
        0.5,
        0,
      ],
    },
    lreProperties: {
      layerGroup: "huc-10-boundaries",
    },
    drawOrder: 99,
  };

  const huc10Line = {
    id: "huc-10-boundaries-line",
    name: "HUC 10 Boundaries",
    type: "line",
    source: "huc-10-boundaries",
    "source-layer": "Yampa_HUC10-c6nelo",
    paint: {
      "line-color": "#60BAF0",
      "line-width": 2,
    },
    lreProperties: {
      layerGroup: "huc-10-boundaries",
    },
    drawOrder: 99,
  };

  const { data, isLoading, error } = useQuery(
    ["wells"],
    async () => {
      let headers = {};

      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        headers = { Authorization: `Bearer ${token}` };
      }
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/wells`,
          { headers }
        );

        return data.filter((location) => location.location_geometry);
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/" + DUMMY_BASEMAP_LAYERS[0].url,
      center: STARTING_LOCATION,
      zoom: 7,
    });

    map.addControl(new mapboxgl.NavigationControl(), "top-left");
    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        // When active the map will receive updates to the device's location as it changes.
        trackUserLocation: true,
        // Draw an arrow next to the location dot to indicate which direction the device is heading.
        showUserHeading: true,
      }),
      "top-left"
    );
    map.addControl(new mapboxgl.FullscreenControl());
    // Add locate control to the map.
    map.addControl(new ResetZoomControl(), "top-left");

    DUMMY_BASEMAP_LAYERS.forEach((layer) => {
      return map.addControl(new ToggleBasemapControl(layer.url, layer.icon));
    });

    map.on("load", () => {
      setMapIsLoaded(true);
      setMap(map);
    });
  }, []); // eslint-disable-line

  //resizes map when mapContainerRef dimensions changes (sidebar toggle)
  useEffect(() => {
    if (map) {
      const resizer = new ResizeObserver(debounce(() => map.resize(), 100));
      resizer.observe(mapContainer.current);
      return () => {
        resizer.disconnect();
      };
    }
  }, [map]);

  useEffect(() => {
    if (mapIsLoaded && data?.length > 0 && typeof map != "undefined") {
      if (!map.getSource("locations")) {
        map.addSource("huc-10-boundaries", {
          type: "vector",
          url: "mapbox://yampariver.4r6uwl8q",
        });

        map.addLayer(huc10Fill);
        map.addLayer(huc10Line);

        map.addSource("locations", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: data.map((location) => {
              return {
                id: location.location_ndx,
                type: "Feature",
                properties: {
                  description: location.location_name,
                  id: location.location_id,
                  index: location.location_ndx,
                  locType: location.locationtype,
                  parameterGroups:
                    location?.parameter_groups?.join(", ") || null,
                  huc8: location.huc8,
                  huc10: location.huc10,
                  huc10_name: location.huc10_name,
                  huc12: location.huc12,
                  huc12_name: location.huc12_name,
                },
                geometry: {
                  type: location.location_geometry.type,
                  coordinates: location.location_geometry.coordinates,
                },
              };
            }),
          },
        });
        // Add a layer showing the places.
        map.addLayer(locationsLayer);
        map.addLayer(locationsLabelsLayer);

        map.on("click", "huc-10-boundaries-fill", (e) => {
          const feature = map
            .queryRenderedFeatures(e.point)
            .filter((feature) => feature?.properties?.Name)[0];

          const description = feature.properties.Name;

          const huc10Popup = new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(description)
            .addTo(map);

          map.on("closeAllPopups", () => {
            huc10Popup.remove();
          });
        });

        map.on("click", "locations", (e) => {
          map.fire("closeAllPopups");

          const features = map.queryRenderedFeatures(e.point);
          const myFeatures = features.filter(
            (feature) => feature.source === "locations"
          );
          const coordinates = [e.lngLat.lng, e.lngLat.lat];

          const popupNode = document.createElement("div");
          ReactDOM.render(
            //MJB adding style providers to the popup
            <StylesProvider jss={jss}>
              <MuiThemeProvider theme={createTheme(theme.currentTheme)}>
                <ThemeProvider theme={createTheme(theme.currentTheme)}>
                  <Popup
                    layers={[locationsLayer]}
                    features={myFeatures}
                    maxWidth="252px"
                    size="small"
                  />
                </ThemeProvider>
              </MuiThemeProvider>
            </StylesProvider>,
            popupNode
          );
          popUpRef.current
            .setLngLat(coordinates)
            .setDOMContent(popupNode)
            .addTo(map);
        });

        // Change the cursor to a pointer when the mouse is over the places layer.
        map.on("mouseenter", "locations", () => {
          map.getCanvas().style.cursor = "pointer";
        });

        // Change it back to a pointer when it leaves.
        map.on("mouseleave", "locations", () => {
          map.getCanvas().style.cursor = "";
        });
      }
    }
  }, [isLoading, mapIsLoaded, map, data]); //eslint-disable-line

  //filters the table based on the selected radioValues filters
  useEffect(() => {
    if (map !== undefined && map.getLayer("locations")) {
      map.setFilter("locations", [
        "in",
        ["get", "index"],
        ["literal", selectedHuc10Locations],
      ]);
      map.setFilter("locations-labels", [
        "in",
        ["get", "index"],
        ["literal", selectedHuc10Locations],
      ]);
    }
  }, [selectedHuc10Locations]); // eslint-disable-line

  useEffect(() => {
    if (map !== undefined && map.getLayer("locations")) {
      map.setPaintProperty("locations", "circle-stroke-width", [
        "case",
        ["in", ["get", "index"], ["literal", selectedLeftLocations]],
        5,
        ["in", ["get", "index"], ["literal", selectedRightLocations]],
        5,
        1,
      ]);
      map.setPaintProperty("locations", "circle-stroke-color", [
        "case",
        [
          "all",
          ["in", ["get", "index"], ["literal", selectedLeftLocations]],
          ["in", ["get", "index"], ["literal", selectedRightLocations]],
        ],
        lineColors.orange,
        ["in", ["get", "index"], ["literal", selectedLeftLocations]],
        lineColors.yellow,
        ["in", ["get", "index"], ["literal", selectedRightLocations]],
        lineColors.red,
        "black",
      ]);
    }
  }, [selectedLeftLocations, selectedRightLocations]); //eslint-disable-line

  if (error) return "An error has occurred: " + error.message;

  return (
    <Root>
      <MapContainer ref={mapContainer} />
    </Root>
  );
};

export default TimeseriesComparisonMap;
